import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Invoiceicon from "../../../assets/img/totalCountsIcon.png";

export type HighlightDistributionBin = { length: number; count: number };

type Props = {
  title?: string;
  loading: boolean;
  error: string | null;
  rows: HighlightDistributionBin[];
  maxX?: number; // default 60 (dhe 60+ bucket)
};

/** -------- helpers për Y-axis "nice ticks" -------- */
const niceStep = (max: number) => {
  if (max <= 10) return 1;
  if (max <= 50) return 5;
  if (max <= 100) return 10;
  if (max <= 250) return 25;
  if (max <= 500) return 50;
  if (max <= 1000) return 100;
  return 200;
};

const niceCeil = (max: number, step: number) => Math.ceil(max / step) * step;

const clamp = (n: number, min: number, max: number) =>
  Math.max(min, Math.min(max, n));

const HighlightDistributionByLength: React.FC<Props> = ({
  title: titleProp,
  loading,
  error,
  rows,
  maxX = 60,
}) => {
  const { t } = useTranslation("docProcessing");
  const title = titleProp ?? t("highlightDistribution.title");
  const [hovered, setHovered] = useState<{
    x: number;
    y: number;
    lengthLabel: string;
    count: number;
  } | null>(null);

  const BAR_AREA_H = 170; // px (si figma)
  const MIN_BAR_H = 2; // px

  const normalized = useMemo(() => {
    const clean = (rows || [])
      .map((x) => ({
        length: Number((x as any)?.length ?? 0),
        count: Number((x as any)?.count ?? 0),
      }))
      .filter(
        (x) =>
          Number.isFinite(x.length) &&
          x.length > 0 &&
          Number.isFinite(x.count) &&
          x.count >= 0
      )
      .sort((a, b) => a.length - b.length);

    // bucket 60+
    const bucketMap = new Map<number, number>();
    for (const r of clean) {
      const key = r.length > maxX ? maxX + 1 : r.length;
      bucketMap.set(key, (bucketMap.get(key) ?? 0) + r.count);
    }

    // 1..maxX plus bucket (maxX+1)
    const maxLength = maxX + 1;
    const bins: HighlightDistributionBin[] = Array.from(
      { length: maxLength },
      (_, i) => {
        const length = i + 1;
        return { length, count: bucketMap.get(length) ?? 0 };
      }
    );

    const maxCount = bins.reduce((m, r) => Math.max(m, r.count), 0) || 0;

    return { bins, maxCount, maxLength };
  }, [rows, maxX]);

  /** -------- Y axis computed from data -------- */
  const yAxis = useMemo(() => {
    const max = normalized.maxCount || 0;
    const step = niceStep(max);

    // if max = 0, still show something meaningful
    const yMax = Math.max(step, niceCeil(max, step));

    const ticks = Array.from(
      { length: Math.round(yMax / step) + 1 },
      (_, i) => i * step
    );

    return { yMax, step, ticks };
  }, [normalized.maxCount]);

  // for display (top -> bottom)
  const yTicks = useMemo(() => [...yAxis.ticks].reverse(), [yAxis.ticks]);

  const showChart = !loading && !error && normalized.bins.length > 0;

  return (
    <div className="chart card" style={{ marginTop: 16 }}>
      <div className="chart-frame highlight-distribution-by-length">
        <h3>{title}</h3>

        {loading && <span className="muted">{t("common.loading")}</span>}
        {error && <span className="error">{error}</span>}
        {!loading && !error && normalized.bins.length === 0 && (
          <span className="muted">{t("common.noData")}</span>
        )}

        {showChart && (
          <div className="hd-wrap" style={{ position: "relative" }}>
            {/* Y axis */}
            <div className="hd-y">
              {yTicks.map((t) => (
                <div key={t} className="hd-y-tick">
                  {t}
                </div>
              ))}
            </div>

            {/* Plot */}
            <div
              className="hd-plot"
              style={{
                ["--barAreaH" as any]: `${BAR_AREA_H}px`,
                minWidth: 0,
                overflowX: "auto",
              }}
            >
              {/* grid */}
              <div className="hd-grid">
                {yTicks.map((t) => (
                  <div key={t} className="hd-grid-line" />
                ))}
              </div>

              <div className="hd-bars">
                {normalized.bins.map((b) => {
                  const isBucket = b.length === maxX + 1;
                  const lengthLabel = isBucket ? `${maxX}+` : String(b.length);

                  // scale with yAxis.yMax (NOT normalized.maxCount)
                  const px = Math.round(
                    (b.count / (yAxis.yMax || 1)) * BAR_AREA_H
                  );
                  const barH =
                    b.count === 0 ? MIN_BAR_H : Math.max(MIN_BAR_H, px);

                  return (
                    <div key={b.length} className="hd-bar-col">
                      <div
                        className="hd-bar"
                        style={{
                          height: `${barH}px`,
                          opacity: b.count === 0 ? 0.12 : 0.35,
                        }}
                        onMouseEnter={(e) => {
                          const rect = e.currentTarget.getBoundingClientRect();
                          const x = rect.left + rect.width / 2;
                          const y = rect.top;

                          // keep tooltip on screen (basic clamp)
                          const safeX = clamp(x, 80, window.innerWidth - 80);
                          const safeY = clamp(y, 60, window.innerHeight - 60);

                          setHovered({
                            x: safeX,
                            y: safeY,
                            lengthLabel,
                            count: b.count,
                          });
                        }}
                        onMouseLeave={() => setHovered(null)}
                      />
                      <div className="hd-x-tick">
                        {(() => {
                          if (isBucket) return `${maxX}+`;
                          if (b.length === 1) return "1";
                          if (b.length % 10 === 0) return String(b.length);
                          if (b.length === maxX) return String(maxX);
                          return "";
                        })()}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Tooltip */}
              {hovered && (
                <div
                  className="hd-tooltip"
                  style={{
                    position: "fixed",
                    left: hovered.x,
                    top: hovered.y - 10,
                    transform: "translate(-50%, -100%)",
                    zIndex: 9999,
                    pointerEvents: "none",
                  }}
                >
                  <div className="hd-tooltip-inner">
                    <div className="hd-tt-icon">
                      <img
                        src={Invoiceicon}
                        alt=""
                        style={{ width: 38, height: 38 }}
                      />
                    </div>
                    <div className="hd-tt-text">
                      <div className="hd-tt-title">{t("highlightDistribution.tooltipTitle")}</div>
                      <div className="hd-tt-value">{hovered.lengthLabel}</div>
                      <div className="hd-tt-sub">
                        {t("highlightDistribution.tooltipCount", {
                          value: hovered.count,
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* {showChart && (
          <div className="muted" style={{ marginTop: 10 }}>
            X = length (1 → {maxX}, {maxX}+ bucket) • Y = count (max:{" "}
            {normalized.maxCount})
          </div>
        )} */}
      </div>
    </div>
  );
};

export default HighlightDistributionByLength;
